import React, { useEffect, useState } from 'react';
import {
  Blog,
  City,
  Country,
  LandingComponent,
  Program,
  School,
  Subject,
  SubLandingComponent,
} from 'app/types';
import Text from 'common/components/Text/Text';
import Placeholder from 'assets/explore/placeholder.png';

import { Button } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { useMediaQuery } from 'react-responsive';
import { useAppDispatch } from 'app/hooks';
import { setFilters } from 'features/session/sessionSlice';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { newDetailUrl, detailUrl, formatMonth } from 'common/utils/conversions';
import ProgramCard from 'common/components/ProgramCard/ProgramCard';
import SchoolCard from 'common/components/SchoolCard/SchoolCard';
import styles from '../LandingForm.module.scss';
import rehypeRaw from 'rehype-raw';

interface PreviewComponentProps {
  landing_cmp: LandingComponent;
}

interface StandardCardProps {
  id: number;
  image?: string;
  logo?: string;
  name: string;
  handleSubjectFilter: (subject_id: number) => void;
  type?: string;
  navigate: NavigateFunction;
  link?: string;
  date?: string;
}

const StandardCard = ({
  id,
  image,
  name,
  handleSubjectFilter,
  type,
  navigate,
  logo,
  link,
}: StandardCardProps): React.ReactElement => {
  const handleClick = () => {
    if (type === 'program') {
      navigate(newDetailUrl('programs', id, name));
    } else if (type === 'school') {
      navigate(newDetailUrl('schools', id, name));
    } else if (type === 'country') {
      navigate(detailUrl('countries', id, name));
    } else if (type === 'city') {
      navigate(detailUrl('cities', id, name));
    } else if (type === 'blog' && link) {
      window.open(link);
    }
  };
  return (
    <div
      className={`card ${styles.contentGridCard}`}
      style={{
        color: image ? 'white' : '#297DF2',
        backgroundImage: image
          ? `linear-gradient( to bottom, #19367600, #00000099 ), url(${image})`
          : `url(${Placeholder})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      onClick={type === 'subject' ? () => handleSubjectFilter(id) : handleClick}
    >
      <div style={{ alignSelf: 'flex-start', zIndex: '10' }}>{name}</div>
      {logo && (
        <div
          style={{
            position: 'absolute',
            left: '25px',
            top: '25px',
            height: '60px',
            width: '60px',
            background: 'white',
            borderRadius: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <img src={logo} style={{ maxWidth: '100%', maxHeight: '100%' }}></img>
        </div>
      )}
    </div>
  );
};

const BlogCard = ({ image, name, link, date }: StandardCardProps): React.ReactElement => {
  const formattedDate = date ? new Date(date.replace(/-/g, '/')) : undefined;
  return (
    <div className={`card ${styles.blogCard}`} onClick={() => window.open(link)}>
      <img
        src={image ? image : Placeholder}
        style={{
          borderRadius: '10px',
          maxWidth: '100%',
          height: '200px',
          objectFit: 'cover',
          marginBottom: '8px',
        }}
      ></img>
      <div style={{ alignSelf: 'flex-start', marginBottom: '8px' }}>{name}</div>
      {date && (
        <div
          style={{ alignSelf: 'flex-start', fontSize: '16px', fontWeight: '600', color: '#727D9B' }}
        >
          {`${formatMonth(
            formattedDate
          )} ${formattedDate?.getDate()}, ${formattedDate?.getFullYear()}`}
        </div>
      )}
    </div>
  );
};

const ContentGridPreview = ({ landing_cmp }: PreviewComponentProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<number>(0);
  const [activeFilterName, setActiveFilterName] = useState<string | undefined>(
    landing_cmp.sub_landing_components[0]?.filters?.[0]
  );
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const buttonStyle = (style?: string) => {
    if (style === 'secondary') {
      return styles.secondaryButton;
    } else if (style === 'light') {
      return styles.lightButton;
    } else {
      return styles.primaryButton;
    }
  };

  const onlyProgramsWithFilter = () => {
    return (
      landing_cmp.sub_landing_components.length === 1 &&
      landing_cmp.sub_landing_components[0].config.tab_type === 'programs' &&
      landing_cmp.sub_landing_components[0].config.button_filter_type &&
      landing_cmp.sub_landing_components[0].filters
    );
  };

  const handleSubjectFilter = (subject_id: number) => {
    dispatch(setFilters({ subject_id: [subject_id] }));
    navigate('/explore/programs');
  };

  useEffect(() => {
    if (landing_cmp.sub_landing_components[0].config.button_filter_type === 'country') {
      setFilteredPrograms(
        landing_cmp.sub_landing_components[0].programs.filter(
          (program: Program) => program.country_name === activeFilterName
        )
      );
    } else if (landing_cmp.sub_landing_components[0].config.button_filter_type === 'subject') {
      setFilteredPrograms(
        landing_cmp.sub_landing_components[0].programs.filter((program: Program) =>
          program.subjects.some((subject: Subject) => subject.name === activeFilterName)
        )
      );
    } else if (landing_cmp.sub_landing_components[0].config.button_filter_type === 'degree') {
      if (activeFilterName === 'Postgraduate') {
        setFilteredPrograms(
          landing_cmp.sub_landing_components[0].programs.filter(
            (program: Program) => program.degree_type === 'postgrad'
          )
        );
      } else if (activeFilterName === 'Undergraduate') {
        setFilteredPrograms(
          landing_cmp.sub_landing_components[0].programs.filter(
            (program: Program) => program.degree_type === 'undergrad'
          )
        );
      }
    }
  }, [activeFilterName]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: landing_cmp.size !== 'sm' && landing_cmp.size !== 'md' ? landing_cmp.size : 'auto',
        padding: landing_cmp.size === 'sm' ? '10px 12rem' : '10px 4vw',
        margin: landing_cmp.gap,
        background: !(landing_cmp.background instanceof File) ? landing_cmp.background : 'auto',
        backgroundSize: landing_cmp.background_size,
        backgroundImage: `url(${landing_cmp.background_image})`,
        gap: '44px',
      }}
      className={landing_cmp.class_name}
      key={landing_cmp.id}
    >
      {(landing_cmp.config.title || landing_cmp.config.section_title) && (
        <div className="headerSection" style={{ width: '55%' }}>
          {landing_cmp.config.section_title && (
            <div className={`${styles.sectionTitle} text-center d-flex flex-column`}>
              {landing_cmp.config.section_title}
              <div
                style={{ width: '87px', height: '8px', background: '#E8F3FE', alignSelf: 'center' }}
              ></div>
            </div>
          )}
          {landing_cmp.config.title && (
            <div
              style={{
                fontSize: '40px',
                color: '#193676',
                fontWeight: '700',
                textAlign: 'center',
              }}
            >
              {landing_cmp.config.title}
            </div>
          )}
        </div>
      )}
      {landing_cmp.config.description && (
        <Text
          size={16}
          weight={400}
          inlineStyle={{
            color: '#19234F',
            textAlign: 'center',
            maxWidth: '55%',
          }}
        >
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {landing_cmp.config.description}
          </ReactMarkdown>
        </Text>
      )}
      {landing_cmp.sub_landing_components.length > 1 && (
        <div
          className={`row flex-${
            isMobile ? 'column' : 'row'
          } flex-nowrap gap-5 justify-content-center align-items-center`}
        >
          {landing_cmp.sub_landing_components.map((tab: SubLandingComponent, index: number) => (
            <div
              key={tab.id}
              className={activeTab === index ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab(index)}
              data-id={tab.id}
            >
              {tab.config.tab_name}
            </div>
          ))}
        </div>
      )}
      {onlyProgramsWithFilter() && (
        <>
          <div className="row flex-row flex-nowrap gap-5 justify-content-center align-items-center">
            {landing_cmp.sub_landing_components[0].filters.map((filter: string, index: number) => (
              <div
                key={index}
                className={activeFilter === index ? styles.tabActive : styles.tab}
                onClick={() => {
                  setActiveFilter(index);
                  setActiveFilterName(filter);
                }}
              >
                {filter}
              </div>
            ))}
          </div>
          <div className={`row row-cols-1 row-cols-md-2 row-cols-lg-4 w-100`}>
            {filteredPrograms.map((program: Program) => (
              <div className="col" style={{ padding: '10px' }} key={program.id}>
                <StandardCard
                  id={program.id}
                  name={program.name}
                  image={program.photo}
                  navigate={navigate}
                  handleSubjectFilter={handleSubjectFilter}
                  logo={program.logo}
                  type="program"
                />
              </div>
            ))}
          </div>
        </>
      )}

      {!onlyProgramsWithFilter() &&
        landing_cmp.sub_landing_components.map(
          (tab: SubLandingComponent, index: number) =>
            activeTab === index && (
              <div key={index} className={`${index}`}>
                <div key={tab.id} className={`row row-cols-1 row-cols-md-2 row-cols-lg-4 w-100`}>
                  {tab.config.tab_type === 'subjects' &&
                    tab.subjects &&
                    tab.subjects.length > 0 &&
                    tab.subjects.map((subject: Subject) => (
                      <div className="col" style={{ padding: '10px' }} key={subject.id}>
                        <StandardCard
                          id={subject.id}
                          name={subject.name}
                          image={subject.image}
                          navigate={navigate}
                          handleSubjectFilter={handleSubjectFilter}
                          type="subject"
                        />
                      </div>
                    ))}
                  {tab.config.tab_type === 'cities' &&
                    tab.cities &&
                    tab.cities.length > 0 &&
                    tab.cities.map((city: City) => (
                      <div className="col" style={{ padding: '10px' }} key={city.id}>
                        <StandardCard
                          id={city.id}
                          name={city.name}
                          image={city.photo}
                          navigate={navigate}
                          handleSubjectFilter={handleSubjectFilter}
                          type="city"
                        />
                      </div>
                    ))}
                  {tab.config.tab_type === 'countries' &&
                    tab.countries &&
                    tab.countries.length > 0 &&
                    tab.countries.map((country: Country) => (
                      <div className="col" style={{ padding: '10px' }} key={country.id}>
                        <StandardCard
                          id={country.id}
                          name={country.name}
                          image={country.photo}
                          navigate={navigate}
                          handleSubjectFilter={handleSubjectFilter}
                          type="country"
                        />
                      </div>
                    ))}
                  {tab.config.tab_type === 'blogs' &&
                    tab.blogs &&
                    tab.blogs.length > 0 &&
                    tab.blogs.map((blog: Blog, index) => (
                      <div className="col" style={{ padding: '10px' }} key={index}>
                        <BlogCard
                          id={index}
                          name={blog.title}
                          image={blog.image}
                          navigate={navigate}
                          handleSubjectFilter={handleSubjectFilter}
                          type="blog"
                          link={blog.url}
                          date={blog.published}
                        />
                      </div>
                    ))}
                  {tab.config.tab_type === 'programs' &&
                    tab.programs &&
                    tab.programs.length > 0 &&
                    tab.programs.map((program: Program) =>
                      tab.config.tab_style === 'explore' ? (
                        <div className="col" style={{ padding: '10px' }} key={program.id}>
                          <ProgramCard program={program} />
                        </div>
                      ) : (
                        <div className="col" style={{ padding: '10px' }} key={program.id}>
                          <StandardCard
                            id={program.id}
                            name={program.name}
                            image={program.photo}
                            navigate={navigate}
                            handleSubjectFilter={handleSubjectFilter}
                            logo={program.logo}
                            type="program"
                          />
                        </div>
                      )
                    )}
                  {tab.config.tab_type === 'schools' &&
                    tab.schools &&
                    tab.schools.length > 0 &&
                    tab.schools.map((school: School) =>
                      tab.config.tab_style === 'explore' ? (
                        <div className="col" style={{ padding: '10px' }} key={school.id}>
                          <SchoolCard school={school} />
                        </div>
                      ) : (
                        <div className="col" style={{ padding: '10px' }} key={school.id}>
                          <StandardCard
                            id={school.id}
                            name={school.name}
                            image={school.photo}
                            navigate={navigate}
                            handleSubjectFilter={handleSubjectFilter}
                            logo={school.logo}
                            type="school"
                          />
                        </div>
                      )
                    )}
                </div>
                {tab.config.button_text && tab.config.button_link && (
                  <Button
                    href={tab.config.button_link}
                    className={buttonStyle(tab.config.button_style)}
                  >
                    {tab.config.button_text}
                  </Button>
                )}
              </div>
            )
        )}
    </div>
  );
};

export default ContentGridPreview;
